import React, { useState, useEffect } from 'react';
import { Card, Col, Row, Table, Spin, Typography, Statistic } from 'antd';
import { UsergroupAddOutlined, UserOutlined, UserDeleteOutlined, CalendarOutlined, TrophyOutlined, DashboardOutlined } from '@ant-design/icons';
import axios from 'axios';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, LineChart, Line, Area, AreaChart } from 'recharts';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

export const Axios = axios.create({
	baseURL: process.env.REACT_APP_APPI_ADDRESS
});

const PageWelcome = () => {
	const { t } = useTranslation();
	const [data, setData] = useState([]);
	const [totals, setTotals] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const headers = {
		'accept': '*/*',
		'Authorization': 'Bearer ' + sessionStorage.getItem('token'),
		'Content-Type': 'application/json',
	};

	// Obtener información del usuario
	const getUserInfo = () => {
		const infoUserStr = sessionStorage.getItem('info_user');
		if (infoUserStr) {
			try {
				return JSON.parse(infoUserStr);
			} catch (e) {
				return null;
			}
		}
		return null;
	};

	const userInfo = getUserInfo();

	useEffect(() => {
		const fetchData = async () => {
			try {
				setLoading(true);
				const response = await Axios.get('/mts/dashboard', { headers });
				const responseData = response.data.data;

				if (responseData.length > 0) {
					const totalData = {
						total_usuarios: responseData[0].total_usuarios,
						usuarios_activos: responseData[0].usuarios_activos,
						usuarios_inactivos: responseData[0].usuarios_inactivos,
						usuarios_registrados_ultimos_7_dias: responseData[0].usuarios_registrados_ultimos_7_dias
					};
					setTotals(totalData);
					setData(responseData);
				}
			} catch (error) {
				console.error('Error fetching data:', error);
				setError(error.message);
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, []);

	if (loading) {
		return (
			<div style={{
				display: 'flex',
				justifyContent: 'center',
				alignItems: 'center',
				minHeight: '60vh',
				background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
			}}>
				<Spin size="large" tip="Cargando dashboard..." />
			</div>
		);
	}

	if (error) {
		return (
			<div style={{
				textAlign: 'center',
				padding: '50px',
				background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
				minHeight: '60vh'
			}}>
				<Title level={3} type="danger">Error al cargar el dashboard</Title>
				<Text>{error}</Text>
			</div>
		);
	}

	// Definición de columnas mejoradas para la tabla
	const columns = [
		{
			title: '👤 Rol de Usuario',
			dataIndex: 'denominacion',
			key: 'denominacion',
			render: (text) => (
				<Text strong style={{ color: '#253289' }}>{text}</Text>
			),
		},
		{
			title: '📊 Cantidad',
			dataIndex: 'cantidad_usuarios_por_rol',
			key: 'cantidad_usuarios_por_rol',
			render: (value) => (
				<div style={{ textAlign: 'center' }}>
					<Statistic
						value={value}
						valueStyle={{
							color: '#52c41a',
							fontSize: '16px',
							fontWeight: 'bold'
						}}
					/>
				</div>
			),
		}
	];

	// Datos para gráficos
	const pieData = data.map(item => ({
		name: item.denominacion,
		value: item.cantidad_usuarios_por_rol
	}));

	const barData = data.map(item => ({
		name: item.denominacion.length > 10 ?
			item.denominacion.substring(0, 10) + '...' :
			item.denominacion,
		value: item.cantidad_usuarios_por_rol,
		fullName: item.denominacion
	}));

	// Colores modernos y profesionales
	const COLORS = ['#253289', '#4a5acf', '#1e237a', '#3b4edc', '#6b73db', '#8a90e0'];

	// Función para obtener el rol más popular
	const getMostPopularRole = () => {
		if (data.length === 0) return null;
		return data.reduce((prev, current) =>
			prev.cantidad_usuarios_por_rol > current.cantidad_usuarios_por_rol ? prev : current
		);
	};

	const mostPopularRole = getMostPopularRole();

	// Calcular métricas adicionales
	const activeUsersPercentage = totals ? Math.round((totals.usuarios_activos / totals.total_usuarios) * 100) : 0;
	const growthRate = totals ? Math.round((totals.usuarios_registrados_ultimos_7_dias / totals.total_usuarios) * 100) : 0;

	// Datos simulados para el gráfico de tendencia (últimos 7 días)
	const trendData = [
		{ day: 'Hace 7 días', usuarios: totals ? Math.max(0, totals.usuarios_registrados_ultimos_7_dias - 6) : 0 },
		{ day: 'Hace 6 días', usuarios: totals ? Math.max(0, totals.usuarios_registrados_ultimos_7_dias - 5) : 0 },
		{ day: 'Hace 5 días', usuarios: totals ? Math.max(0, totals.usuarios_registrados_ultimos_7_dias - 4) : 0 },
		{ day: 'Hace 4 días', usuarios: totals ? Math.max(0, totals.usuarios_registrados_ultimos_7_dias - 3) : 0 },
		{ day: 'Hace 3 días', usuarios: totals ? Math.max(0, totals.usuarios_registrados_ultimos_7_dias - 2) : 0 },
		{ day: 'Hace 2 días', usuarios: totals ? Math.max(0, totals.usuarios_registrados_ultimos_7_dias - 1) : 0 },
		{ day: 'Hoy', usuarios: totals ? totals.usuarios_registrados_ultimos_7_dias : 0 }
	];

	return (
		<div style={{
			minHeight: '100vh',
			background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
			padding: '16px'
		}}>
			{/* Header de Bienvenida */}
			<div style={{
				background: 'linear-gradient(135deg, #253289 0%, #4a5acf 100%)',
				borderRadius: '8px',
				padding: '12px',
				marginBottom: '12px',
				color: 'white',
				boxShadow: '0 2px 8px rgba(37, 50, 137, 0.3)'
			}}>
				<Row align="middle">
					<Col xs={24} md={16}>
						<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
							<DashboardOutlined style={{ fontSize: '24px' }} />
							<div>
								<Title level={4} style={{ color: 'white', margin: 0, marginBottom: '2px' }}>
									¡Bienvenido, {userInfo?.nombreusuario || 'Usuario'}!
								</Title>
								<Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>
									Dashboard Smart Delivery
								</Text>
							</div>
						</div>
					</Col>
					<Col xs={24} md={8} style={{ textAlign: 'right' }}>
						<div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
							<div style={{
								background: 'rgba(255,255,255,0.15)',
								borderRadius: '4px',
								padding: '2px 6px',
								backdropFilter: 'blur(10px)'
							}}>
								<Text style={{ color: 'white', fontSize: '10px' }}>
									📊 Activos: {activeUsersPercentage}%
								</Text>
							</div>
							<div style={{
								background: 'rgba(255,255,255,0.15)',
								borderRadius: '4px',
								padding: '2px 6px',
								backdropFilter: 'blur(10px)'
							}}>
								<Text style={{ color: 'white', fontSize: '10px' }}>
									📈 Nuevos: {growthRate}%
								</Text>
							</div>
							<div style={{
								background: 'rgba(255,255,255,0.2)',
								borderRadius: '4px',
								padding: '4px 6px',
								backdropFilter: 'blur(10px)'
							}}>
								<Text style={{ color: 'white', fontSize: '10px' }}>
									👤 Rol: {userInfo?.rol || 'Usuario'}
								</Text>
							</div>
						</div>
					</Col>
				</Row>
			</div>

			{/* Cards de Estadísticas Principales */}
			<Row gutter={[12, 12]} style={{ marginBottom: '12px' }}>
				<Col xs={24} sm={12} lg={6}>
					<Card
						style={{
							background: 'linear-gradient(135deg, #253289 0%, #4a5acf 100%)',
							border: 'none',
							borderRadius: '8px',
							boxShadow: '0 2px 8px rgba(37, 50, 137, 0.3)',
							transition: 'transform 0.3s ease',
							cursor: 'pointer',
							height: '100px'
						}}
						bodyStyle={{ padding: '12px' }}
						onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
						onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
					>
						<div style={{ textAlign: 'center', color: 'white' }}>
							<UsergroupAddOutlined style={{ fontSize: '20px', marginBottom: '4px' }} />
							<Statistic
								title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '10px' }}>Total Usuarios</span>}
								value={totals.total_usuarios}
								valueStyle={{ color: 'white', fontSize: '18px', fontWeight: 'bold' }}
							/>
						</div>
					</Card>
				</Col>

				<Col xs={24} sm={12} lg={6}>
					<Card
						style={{
							background: 'linear-gradient(135deg, #253289 0%, #4a5acf 100%)',
							border: 'none',
							borderRadius: '8px',
							boxShadow: '0 2px 8px rgba(37, 50, 137, 0.3)',
							transition: 'transform 0.3s ease',
							cursor: 'pointer',
							height: '100px'
						}}
						bodyStyle={{ padding: '12px' }}
						onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
						onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
					>
						<div style={{ textAlign: 'center', color: 'white' }}>
							<UserOutlined style={{ fontSize: '20px', marginBottom: '4px' }} />
							<Statistic
								title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '10px' }}>Activos</span>}
								value={totals.usuarios_activos}
								valueStyle={{ color: 'white', fontSize: '18px', fontWeight: 'bold' }}
							/>
						</div>
					</Card>
				</Col>

				<Col xs={24} sm={12} lg={6}>
					<Card
						style={{
							background: 'linear-gradient(135deg, #253289 0%, #4a5acf 100%)',
							border: 'none',
							borderRadius: '8px',
							boxShadow: '0 2px 8px rgba(37, 50, 137, 0.3)',
							transition: 'transform 0.3s ease',
							cursor: 'pointer',
							height: '100px'
						}}
						bodyStyle={{ padding: '12px' }}
						onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
						onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
					>
						<div style={{ textAlign: 'center', color: 'white' }}>
							<UserDeleteOutlined style={{ fontSize: '20px', marginBottom: '4px' }} />
							<Statistic
								title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '10px' }}>Inactivos</span>}
								value={totals.usuarios_inactivos}
								valueStyle={{ color: 'white', fontSize: '18px', fontWeight: 'bold' }}
							/>
						</div>
					</Card>
				</Col>

				<Col xs={24} sm={12} lg={6}>
					<Card
						style={{
							background: 'linear-gradient(135deg, #253289 0%, #4a5acf 100%)',
							border: 'none',
							borderRadius: '8px',
							boxShadow: '0 2px 8px rgba(37, 50, 137, 0.3)',
							transition: 'transform 0.3s ease',
							cursor: 'pointer',
							height: '100px'
						}}
						bodyStyle={{ padding: '12px' }}
						onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-1px)'}
						onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
					>
						<div style={{ textAlign: 'center', color: 'white' }}>
							<CalendarOutlined style={{ fontSize: '20px', marginBottom: '4px' }} />
							<Statistic
								title={<span style={{ color: 'rgba(255,255,255,0.9)', fontSize: '10px' }}>Nuevos (7d)</span>}
								value={totals.usuarios_registrados_ultimos_7_dias}
								valueStyle={{ color: 'white', fontSize: '18px', fontWeight: 'bold' }}
							/>
						</div>
					</Card>
				</Col>
			</Row>

			{/* Card de Rol más Popular */}
			{mostPopularRole && (
				<Row gutter={[12, 12]} style={{ marginBottom: '12px' }}>
					<Col span={24}>
						<Card
							style={{
								background: 'linear-gradient(135deg, #253289 0%, #3b4edc 100%)',
								border: 'none',
								borderRadius: '8px',
								boxShadow: '0 2px 8px rgba(37, 50, 137, 0.2)',
								height: '60px'
							}}
							bodyStyle={{ padding: '12px' }}
						>
							<Row align="middle" style={{ color: 'white' }}>
								<Col>
									<TrophyOutlined style={{ fontSize: '24px', marginRight: '8px' }} />
								</Col>
								<Col flex={1}>
									<Title level={5} style={{ color: 'white', margin: 0, fontSize: '14px' }}>
										🏆 Rol Popular: {mostPopularRole.denominacion}
									</Title>
									<Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: '10px' }}>
										{mostPopularRole.cantidad_usuarios_por_rol} usuarios
									</Text>
								</Col>
							</Row>
						</Card>
					</Col>
				</Row>
			)}

			{/* Sección de Gráficos y Tablas */}
			<Row gutter={[16, 16]}>
				<Col xs={24} lg={12}>
					<Card
						title={
							<span style={{ color: '#253289', fontSize: '16px', fontWeight: 'bold' }}>
								📊 Distribución por Rol
							</span>
						}
						style={{
							borderRadius: '12px',
							boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
							border: 'none',
							height: '340px'
						}}
						bodyStyle={{ padding: '16px', height: 'calc(100% - 57px)', display: 'flex', flexDirection: 'column' }}
					>
						<div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
							<Table
								dataSource={data}
								columns={columns}
								rowKey="denominacion"
								pagination={false}
								size="small"
								style={{ marginTop: '8px', width: '100%' }}
							/>
						</div>
					</Card>
				</Col>

				<Col xs={24} lg={12}>
					<Card
						title={
							<span style={{ color: '#253289', fontSize: '16px', fontWeight: 'bold' }}>
								🥧 Gráfico Circular
							</span>
						}
						style={{
							borderRadius: '12px',
							boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
							border: 'none',
							height: '340px'
						}}
						bodyStyle={{ padding: '16px', height: 'calc(100% - 57px)' }}
					>
						<ResponsiveContainer width="100%" height="100%">
							<PieChart>
								<Pie
									data={pieData}
									dataKey="value"
									nameKey="name"
									cx="50%"
									cy="45%"
									outerRadius={45}
									innerRadius={0}
									label={({ name, percent }) => {
										const percentage = (percent * 100).toFixed(0);
										return percent > 0.02 ? `${name}: ${percentage}%` : '';
									}}
									labelLine={true}
									fontSize={9}
									fill="#8884d8"
								>
									{pieData.map((entry, index) => (
										<Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
									))}
								</Pie>
								<Tooltip
									formatter={(value, name) => [value, 'Usuarios']}
									labelFormatter={(name) => `Rol: ${name}`}
								/>
								<Legend
									verticalAlign="bottom"
									height={35}
									iconSize={6}
									wrapperStyle={{
										fontSize: '10px',
										paddingTop: '5px',
										lineHeight: '1.1'
									}}
									formatter={(value) => value.length > 15 ? value.substring(0, 15) + '...' : value}
								/>
							</PieChart>
						</ResponsiveContainer>
					</Card>
				</Col>
			</Row>

			{/* Gráficos Adicionales */}
			<Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
				<Col xs={24} lg={12}>
					<Card
						title={
							<span style={{ color: '#253289', fontSize: '16px', fontWeight: 'bold' }}>
								📈 Comparativa por Rol
							</span>
						}
						style={{
							borderRadius: '12px',
							boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
							border: 'none'
						}}
						bodyStyle={{ padding: '16px' }}
					>
						<ResponsiveContainer width="100%" height={250}>
							<BarChart data={barData}>
								<CartesianGrid strokeDasharray="3 3" />
								<XAxis dataKey="name" />
								<YAxis />
								<Tooltip
									formatter={(value, name, props) => [value, 'Usuarios']}
									labelFormatter={(name) => {
										const item = barData.find(d => d.name === name);
										return item ? item.fullName : name;
									}}
								/>
								<Bar dataKey="value" fill="#253289" radius={[4, 4, 0, 0]} />
							</BarChart>
						</ResponsiveContainer>
					</Card>
				</Col>

				<Col xs={24} lg={12}>
					<Card
						title={
							<span style={{ color: '#253289', fontSize: '16px', fontWeight: 'bold' }}>
								📊 Tendencia (7 días)
							</span>
						}
						style={{
							borderRadius: '12px',
							boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
							border: 'none'
						}}
						bodyStyle={{ padding: '16px' }}
					>
						<ResponsiveContainer width="100%" height={250}>
							<AreaChart data={trendData}>
								<defs>
									<linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
										<stop offset="5%" stopColor="#253289" stopOpacity={0.8}/>
										<stop offset="95%" stopColor="#253289" stopOpacity={0.1}/>
									</linearGradient>
								</defs>
								<CartesianGrid strokeDasharray="3 3" />
								<XAxis dataKey="day" />
								<YAxis />
								<Tooltip
									formatter={(value) => [value, 'Usuarios registrados']}
									labelStyle={{ color: '#253289' }}
								/>
								<Area
									type="monotone"
									dataKey="usuarios"
									stroke="#253289"
									strokeWidth={2}
									fillOpacity={1}
									fill="url(#colorUsers)"
								/>
							</AreaChart>
						</ResponsiveContainer>
					</Card>
				</Col>
			</Row>
		</div>
	);
};

export default PageWelcome;






